import { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import {
  ConfigLevelTwo,
  EventsWithBloodPressureConfig,
  EventsWithCountConfig,
  EventsWithDurationConfig,
  EventsWithTimeConfig,
  EventsWithTriggerTime,
  i18textByEventType,
} from "../../helpers/constants";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/appHooks";
import {
  getResidentCity,
  getResidentName,
  isHelpAtHome,
} from "../dashboard/dashboardSlice";
import { UnitConfigItem } from "./UnitConfigItem";
import { EventTypeLevelConfig } from "../../services/settings.services";
import { IUnit } from "../../services/dashboard.services";
import { FilterSearchField } from "../notifications/filters/FilterSearchField";
import { getUnitDisplayName } from "./common/helpers";
import { setConfigSelectedUnits } from "./settingsSlice";
import useHelpAtHome from "../notifications/hooks/useHelpAtHome";
import { VariableSizeList as ListVariable } from "react-window";
import { timeConfigurations } from "../../helpers/datetime";

const useStyles = makeStyles<Theme, IProps>((theme: Theme) =>
  createStyles({
    title: {
      fontFamily: theme.typography.secondaryFontFamily,
    },
    unitList: {
      display: "flex",
      flexDirection: "column",
      maxHeight: "100%",
      overflow: "auto",
    },
    filterContainer: {
      width: "50%",
      padding: theme.spacing(2, 0.5),
    },
    unitConfigContainer: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: (props) => props.height,
      padding: theme.spacing(0, 2, 2, 2),
    },
  })
);

export const UnitsEventConfigList = (props: IProps) => {
  const { eventType, expanded, height } = props;

  /* Refs */
  const listRef = useRef<any | null>();
  const sizeMap = useRef<{ [key: number]: number }>({});

  /* Hooks */
  const classes = useStyles(props);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  /* Selectors */
  const units = useAppSelector((state) => state.headerState.units);

  const unitsEventConfig = useAppSelector(
    (state) => state.settingsState.unitsEventConfig
  );

  const [allHelpAtHome] = useHelpAtHome(units);

  const configListTitle = `${t("unit_event_configurations", {
    eventType: t(i18textByEventType(eventType ?? "")),
  })}`;

  const hasDuration = EventsWithDurationConfig.includes(eventType);
  const hasCount = EventsWithCountConfig.includes(eventType);
  const hasTime = EventsWithTimeConfig.includes(eventType);
  const hasTrigger = EventsWithTriggerTime.includes(eventType);
  const hasBloodPressure = EventsWithBloodPressureConfig.includes(eventType);

  const { timeFormat } = timeConfigurations();

  /* Filtering */
  // Create controlled list options
  let defaultState: { [key: string]: boolean } = {};

  units?.forEach((unit) => (defaultState[unit.id] = false));

  /* ---- State ---- */
  const [filterSelection, setFilterSelection] = useState(defaultState);
  const [filteredOptions, setFilteredOptions] = useState<IUnit[]>([]);

  /* ---- Effects ---- */
  useEffect(() => {
    setFilteredOptions(units ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convertToArray = (obj: { [key: string]: boolean }) => {
    const unitIDs: string[] = [];
    Object.keys(obj).forEach((key) => obj[key] && unitIDs.push(key));
    return unitIDs;
  };

  useEffect(() => {
    setFilterSelection(defaultState);
    dispatch(setConfigSelectedUnits(convertToArray(defaultState)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType]);

  useEffect(() => {
    dispatch(setConfigSelectedUnits(convertToArray(filterSelection)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelection]);

  // Obtain keys for searched for values
  const filteredOptionKeys = filteredOptions.map((option) => option.id);

  // Check if all searched for values are selected
  const allSelected = Object.keys(filterSelection)
    .filter((_key) => filteredOptionKeys.includes(_key))
    .every((val) => filterSelection[val]);

  // Check if all searched for values are not selected
  const noneSelected = Object.keys(filterSelection)
    .filter((_key) => filteredOptionKeys.includes(_key))
    .every((val) => !filterSelection[val]);

  /* ---- Methods ---- */
  const handleChange = (optionID: string) => {
    setFilterSelection((prevState) => {
      return { ...prevState, [optionID]: !prevState[optionID] };
    });
  };

  const handleSelectAll = () => {
    setFilterSelection((prevState) => {
      const keysToUpdate = Object.keys(prevState).filter((option) =>
        filteredOptionKeys.includes(option)
      );
      const newState: {
        [key: string]: boolean;
      } = {};
      if (allSelected) {
        keysToUpdate.forEach((option) => (newState[option as string] = false));
      } else {
        keysToUpdate.forEach((option) => (newState[option as string] = true));
      }
      return { ...prevState, ...newState };
    });
  };

  const handleSearchField = (searchVal: string) => {
    const filteredArray = units?.filter((option) => {
      const isHelpAtHomeUnit = isHelpAtHome(option);
      const unitName = option ? option.name : "-";
      const resident = option
        ? getResidentName(option.residents, {
            fullFirstName: false,
            fullLastName: true,
          })
        : "-";
      const city = option ? getResidentCity(option.residents) : "-";

      if (isHelpAtHomeUnit) {
        return (
          resident.toLowerCase().includes(searchVal) ||
          city.toLowerCase().includes(searchVal)
        );
      } else {
        return (
          unitName.toLowerCase().includes(searchVal) ||
          resident.toLowerCase().includes(searchVal)
        );
      }
    });
    setFilteredOptions(filteredArray ?? []);
  };

  // Virtualized list
  // Scroll to top on event change
  useEffect(() => {
    if (listRef && listRef.current) {
      listRef.current.scrollTo(0);
    }
  }, [eventType]);

  const setSize = useCallback((index, size) => {
    sizeMap.current = { ...sizeMap.current, [index]: size };
    listRef.current.resetAfterIndex(index);
  }, []);

  const getSize = (index: number) => sizeMap.current[index] + 8 || 50;

  return (
    <div id="unit-config-container" className={classes.unitConfigContainer}>
      <Typography className={classes.title} component="span">
        {configListTitle}
      </Typography>
      <div className={classes.filterContainer}>
        <FilterSearchField
          withApplyButton={false}
          placeholder={allHelpAtHome ? "search_resident" : "search_unit"}
          allSelected={allSelected}
          noneSelected={noneSelected}
          selectAllCheckbox={expanded}
          handleSelectAll={handleSelectAll}
          handleSearchField={handleSearchField}
        />
      </div>

      <ListVariable
        ref={listRef}
        height={height}
        width="100%"
        itemCount={filteredOptions.length}
        itemSize={getSize}
        itemData={filteredOptions}
      >
        {({ data, index, style }) => {
          const unitID = data[index].id;

          const unitConfig = unitsEventConfig?.find(
            (config) => config.unit_id === unitID
          );

          let configuration: EventTypeLevelConfig | undefined = undefined;
          if (unitConfig && unitConfig.configuration) {
            configuration = JSON.parse(unitConfig.configuration);
          }

          let unitDisplayName = getUnitDisplayName(data[index]);

          const isEnabled = unitConfig?.is_enabled;

          const durationVal =
            configuration && configuration[ConfigLevelTwo]?.duration;
          const countVal =
            configuration && configuration[ConfigLevelTwo]?.count;
          const allDay =
            configuration && configuration[ConfigLevelTwo]?.all_day;
          const startTime =
            configuration && configuration[ConfigLevelTwo]?.local_time_start;
          const endTime =
            configuration && configuration[ConfigLevelTwo]?.local_time_end;
          const triggerVal =
            configuration && configuration[ConfigLevelTwo]?.local_time_end;

          const levelTwo = configuration && configuration[ConfigLevelTwo];
          const systolicHighVal = levelTwo
            ? levelTwo.systolic_high ?? levelTwo.systolic_max ?? undefined
            : undefined;
          const systolicLowVal = levelTwo
            ? levelTwo.systolic_low ?? levelTwo.systolic_min ?? undefined
            : undefined;
          const diastolicHighVal = levelTwo
            ? levelTwo.diastolic_high ?? levelTwo.diastolic_max ?? undefined
            : undefined;
          const diastolicLowVal = levelTwo
            ? levelTwo.diastolic_low ?? levelTwo.diastolic_min ?? undefined
            : undefined;

          return (
            <div style={style}>
              <UnitConfigItem
                key={unitID}
                id={unitID}
                expanded={expanded}
                primaryText={unitDisplayName.primary}
                secondaryText={unitDisplayName.secondary}
                selected={filterSelection[unitID]}
                isEnabled={!!isEnabled}
                hasDuration={hasDuration}
                hasCount={hasCount}
                hasTime={hasTime}
                hasTrigger={hasTrigger}
                hasBloodPressure={hasBloodPressure}
                durationVal={durationVal ?? undefined}
                countVal={countVal ?? undefined}
                allDayVal={allDay}
                startTimeVal={startTime}
                endTimeVal={endTime}
                triggerVal={triggerVal ?? undefined}
                systolicHighVal={systolicHighVal}
                systolicLowVal={systolicLowVal}
                diastolicHighVal={diastolicHighVal}
                diastolicLowVal={diastolicLowVal}
                timeFormat={timeFormat}
                toggleSelected={handleChange}
                index={index}
                setSize={setSize}
              />
            </div>
          );
        }}
      </ListVariable>
    </div>
  );
};

interface IProps {
  eventType: string;
  expanded: boolean;
  height: number;
}
